import React from "react";
import { GlobalIcon, IconNameT } from "get-life-storybook-ts";
import AvatarIcon from "./AvatarIcon";
import { DOMAIN } from "../../../../../constants/Global";
import { modificationsAvailable } from "./modifications/shared";

type SectionT =
  | "global"
  | "policy"
  | "billing"
  | "profile"
  | "modifications"
  | "help";

interface SidebarProps {
  activeRoute: string;
  policyStatus: "active" | "cancelled";
  /**
   * false = la sesión es una cotización sin póliza (Portal Cliente native): no se
   * ofrecen las secciones de póliza ni el indicador de póliza activa/cancelada.
   */
  hasPolicy?: boolean;
  userName: string;
  isLoading: boolean;
  onNavigate: (route: string) => void;
  onLogout: () => void;
  translate: (key: string) => string;
  collapsed: boolean;
  onToggleCollapse: () => void;
}

interface NavItem {
  label: string;
  icon: IconNameT;
  route: SectionT;
  /** Solo tiene sentido con una póliza. */
  requiresPolicy?: boolean;
}

const Sidebar = ({
  activeRoute,
  policyStatus,
  hasPolicy = true,
  userName,
  isLoading,
  onNavigate,
  onLogout,
  translate,
  collapsed,
  onToggleCollapse,
}: SidebarProps) => {
  const isCancelled = policyStatus === "cancelled";

  const allNavItems: NavItem[] = [
    {
      label: translate("dashboard.menu.global"),
      icon: "Home2Icon",
      route: "global",
    },
    {
      label: translate("dashboard.menu.policy"),
      icon: "FileInvoiceIcon",
      route: "policy",
      requiresPolicy: true,
    },
    {
      label: translate("dashboard.menu.billing"),
      icon: "CreaditCard",
      route: "billing",
      requiresPolicy: true,
    },
    {
      label: translate("dashboard.menu.profile"),
      icon: "UserCircleIcon",
      route: "profile",
    },
    // Self-service policy modifications (backend only supports ES and AXA WL).
    ...(modificationsAvailable(DOMAIN)
      ? ([
          {
            label: translate("dashboard.menu.modifications"),
            icon: "EditIcon",
            route: "modifications",
            requiresPolicy: true,
          },
        ] as NavItem[])
      : []),
    {
      label: translate("dashboard.menu.help"),
      icon: "HelpIcon",
      route: "help",
    },
  ];
  const navItems = allNavItems.filter((item) => hasPolicy || !item.requiresPolicy);

  const btnBase: React.CSSProperties = {
    width: "100%",
    display: "flex",
    alignItems: "center",
    gap: collapsed ? 0 : 12,
    justifyContent: collapsed ? "center" : "flex-start",
    padding: collapsed ? "12px 0" : "12px 12px",
    borderRadius: 8,
    fontSize: 15,
    fontWeight: 500,
    border: "none",
    cursor: isLoading ? "default" : "pointer",
    opacity: isLoading ? 0.5 : 1,
    transition: "background-color 0.15s, color 0.15s",
    backgroundColor: "transparent",
    color: "#4b5563",
  };

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        width: collapsed ? 72 : 280,
        minWidth: collapsed ? 72 : 280,
        backgroundColor: "#fff",
        borderRight: "1px solid #e5e7eb",
        overflow: "hidden",
        transition: "width 0.2s ease, min-width 0.2s ease",
      }}
    >
      {/* Logo + Collapse Toggle */}
      <div
        style={{
          padding: collapsed ? "20px 0" : "20px 24px",
          display: "flex",
          alignItems: "center",
          justifyContent: collapsed ? "center" : "space-between",
        }}
      >
        {!collapsed && <GlobalIcon iconName="LogoLife5Blue" size="M" />}
        <button
          onClick={onToggleCollapse}
          style={{
            border: "none",
            background: "none",
            cursor: "pointer",
            padding: 6,
            borderRadius: 6,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "#9ca3af",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = "#f3f4f6";
            e.currentTarget.style.color = "#374151";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = "transparent";
            e.currentTarget.style.color = "#9ca3af";
          }}
          title={collapsed ? "Expandir menú" : "Colapsar menú"}
        >
          <GlobalIcon
            iconName="MenuIcon"
            size="XS"
            color="currentColor"
          />
        </button>
      </div>

      {/* User Profile Snippet */}
      <div style={{ padding: collapsed ? "12px 8px" : "12px 16px", margin: collapsed ? "0 4px 8px" : "0 12px 8px" }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 12,
            justifyContent: collapsed ? "center" : "flex-start",
          }}
        >
          <div style={{ position: "relative", flexShrink: 0 }}>
            <AvatarIcon className="" />
            {hasPolicy && (
              <span
                style={{
                  position: "absolute",
                  bottom: 0,
                  right: 0,
                  width: 10,
                  height: 10,
                  borderRadius: "50%",
                  border: "2px solid #fff",
                  backgroundColor: isCancelled ? "#f87171" : "#22c55e",
                }}
                title={
                  isCancelled
                    ? translate("newDashboard.redesign.cancelledPolicyBadge")
                    : translate("newDashboard.redesign.activePolicyBadge")
                }
              />
            )}
          </div>
          {!collapsed && (
            <div style={{ flex: 1, minWidth: 0 }}>
              {isLoading ? (
                <div
                  style={{
                    height: 20,
                    width: 112,
                    backgroundColor: "#e5e7eb",
                    borderRadius: 4,
                  }}
                />
              ) : (
                <p
                  style={{
                    fontSize: 15,
                    fontWeight: 600,
                    color: "#111827",
                    margin: 0,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {userName}
                </p>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Navigation */}
      <nav style={{ flex: 1, padding: collapsed ? "8px 8px" : "8px 12px" }}>
        <ul style={{ listStyle: "none", margin: 0, padding: 0, display: "flex", flexDirection: "column", gap: 4 }}>
          {navItems.map((item) => {
            const isActive = activeRoute === item.route;
            return (
              <li key={item.route}>
                <button
                  onClick={() => onNavigate(item.route)}
                  disabled={isLoading}
                  style={{
                    ...btnBase,
                    fontWeight: isActive ? 600 : 500,
                    backgroundColor: isActive ? "var(--light-primary-color, #eef2ff)" : "transparent",
                    color: isActive ? "var(--primary-color, #4338ca)" : "#4b5563",
                  }}
                  onMouseEnter={(e) => {
                    if (!isActive) {
                      e.currentTarget.style.backgroundColor = "#f9fafb";
                      e.currentTarget.style.color = "#111827";
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!isActive) {
                      e.currentTarget.style.backgroundColor = "transparent";
                      e.currentTarget.style.color = "#4b5563";
                    }
                  }}
                  title={collapsed ? item.label : undefined}
                >
                  {/* currentColor: el color del botón (del mercado si está activo). */}
                  <GlobalIcon
                    iconName={item.icon}
                    size="XS"
                    color={isActive ? "currentColor" : "#6b7280"}
                  />
                  {!collapsed && <span>{item.label}</span>}
                </button>
              </li>
            );
          })}
        </ul>
      </nav>

      {/* Bottom: Logout */}
      <div
        style={{
          padding: collapsed ? "8px 8px 24px" : "8px 12px 24px",
          borderTop: "1px solid #f3f4f6",
          marginTop: "auto",
        }}
      >
        <button
          onClick={onLogout}
          disabled={isLoading}
          style={btnBase}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = "#f9fafb";
            e.currentTarget.style.color = "#111827";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = "transparent";
            e.currentTarget.style.color = "#4b5563";
          }}
          title={collapsed ? translate("dashboard.menu.logout") : undefined}
        >
          <GlobalIcon iconName="LogoutIcon" size="XS" color="#6b7280" />
          {!collapsed && <span>{translate("dashboard.menu.logout")}</span>}
        </button>
      </div>
    </div>
  );
};

export default Sidebar;
